import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import { WorkspaceSidebar } from "../../components/workspace/sidebar";
import { MainLandmark } from "../../components/main-landmark";
import { PendingScreen } from "../../components/pending-screen";
import { openIsland } from "../../components/recordings/chat-recording";
import { useStartChat } from "../../lib/chats/use-start-chat";
import { queryClient } from "../../lib/query-client";
import { useDesktopPrepareIsland, useDesktopStartRecording } from "../../lib/recordings/desktop";
import { onboardingStateQueryOptions } from "../../lib/queries/onboarding";

/**
 * The signed-in shell, for people who belong to a firm. Whether they do is settled before the
 * shell is drawn, for the same reason the session is: deciding it on the page meant drawing home
 * and then leaving it for `/onboarding`.
 *
 * Asked of the service, not inferred from the session's organisation pointer. The pointer is not
 * the same question. Someone whose join request was just approved holds a membership while their
 * existing session still points at nothing — `adoptSoleOrganisation`
 * (`apps/auth/src/modules/workspaces/drizzle-store.ts`) only repoints it when a workspace read
 * arrives, and the browser's cached session is older than that write either way. Reading the
 * pointer sent them to `/onboarding`, which resolves `done` from their membership and sends them
 * straight back: a bounce at best and a loop at worst, on the one path a person reaches by being
 * let into a firm.
 */
export const Route = createFileRoute("/_authenticated/_workspace")({
  beforeLoad: async ({ context }) => {
    // A cached "done" lets someone in without a round trip; nothing else is trusted from the
    // cache. Anything short of done goes stale the moment onboarding finishes — creating a firm
    // navigates here without rewriting it, and an approval happens somewhere else entirely — so
    // sending someone away is only ever decided on a fresh answer. Keyed by user, so a second
    // sign-in in this browser never reads the first's.
    const options = onboardingStateQueryOptions(context.session.user.id);
    const cached = queryClient.getQueryData(options.queryKey);
    const onboarding =
      cached?.state === "done" ? cached : await queryClient.fetchQuery(options).catch(() => null);
    // A read that failed must never read as "no organisation": the shell opens, and the account
    // panel on it is where a failure can be seen and retried.
    if (onboarding && onboarding.state !== "done") {
      throw redirect({ to: "/onboarding", replace: true });
    }
  },
  pendingComponent: () => <PendingScreen label="Getting your workspace ready…" />,
  component: WorkspaceShell,
});

function WorkspaceShell() {
  // The desktop tray's Start a recording, as Show on screen with nothing written: a new chat whose
  // recording opens the island at its sharing step, over the new conversation.
  const { showOnScreen } = useStartChat(openIsland);
  useDesktopStartRecording(() => void showOnScreen());
  // The desktop app readies the island's window once the workspace has settled, so a recording's
  // island shows at once.
  useDesktopPrepareIsland();
  return (
    <div className="workspace-shell">
      <WorkspaceSidebar />
      <MainLandmark className="workspace-panel">
        <Outlet />
      </MainLandmark>
    </div>
  );
}
